"use client";

import { useEffect, useState } from "react";
import { isNativeAndroid } from "./capacitorBridge";
import {
  playAcademyProduct,
  playCreditProduct,
  playSubscriptionProduct,
  type PlayGrant,
} from "../../../convex/lib/playBillingCatalog";

export {
  playAcademyProduct,
  playCreditProduct,
  playSubscriptionProduct,
};

type NativeTx = {
  productIdentifier?: string;
  purchaseToken?: string;
  orderId?: string;
  transactionId?: string;
  purchaseState?: string;
  planIdentifier?: string;
};

type CapBridge = {
  nativePromise?: (
    pluginName: string,
    methodName: string,
    options?: Record<string, unknown>,
  ) => Promise<unknown>;
  Plugins?: Record<
    string,
    Record<string, (options?: Record<string, unknown>) => Promise<unknown>>
  >;
};

declare global {
  interface Window {
    __YATISHARA_PLAY_BILLING__?: boolean;
    YatisharaShell?: {
      isPlayBillingBuild?: () => boolean | string;
      setAppearance?: (mode: string) => void;
    };
  }
}

const PLUGIN = "NativePurchases";

export type PlayVerifyInput = {
  productId: string;
  purchaseToken: string;
  orderId?: string;
  basePlanId?: string;
};

/**
 * Every Capacitor Android shell bills digital goods with Google Play.
 * Web stays on Wam/PayPal. Custom amounts are not a Play SKU.
 */
export function shouldUsePlayBilling(): boolean {
  return isNativeAndroid();
}

/** False on the server render so the Wam/Play label does not hydrate twice. */
export function usePlayBilling(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    setEnabled(shouldUsePlayBilling());
  }, []);
  return enabled;
}

async function playCall(
  method: string,
  options?: Record<string, unknown>,
): Promise<unknown> {
  const cap = window.Capacitor as CapBridge | undefined;
  const plugin = cap?.Plugins?.[PLUGIN];
  if (plugin && typeof plugin[method] === "function") {
    return plugin[method](options);
  }
  if (cap?.nativePromise) {
    return cap.nativePromise(PLUGIN, method, options);
  }
  throw new Error("Google Play Billing is not available in this build.");
}

function assertPlayShell() {
  if (!shouldUsePlayBilling()) {
    throw new Error("Google Play Billing is only available in the Play Store app.");
  }
}

export async function startPlayPurchase(args: {
  productId: string;
  productType: "inapp" | "subs";
  basePlanId?: string;
}): Promise<PlayVerifyInput> {
  assertPlayShell();
  const support = (await playCall("isBillingSupported")) as {
    isBillingSupported?: boolean;
  };
  if (!support?.isBillingSupported) {
    throw new Error("Google Play Billing is not available on this device.");
  }
  const tx = (await playCall("purchaseProduct", {
    productIdentifier: args.productId,
    productType: args.productType,
    planIdentifier: args.basePlanId,
    isConsumable: false,
    autoAcknowledgePurchases: false,
  })) as NativeTx;
  if (tx.purchaseState && tx.purchaseState !== "1") {
    throw new Error("Google Play purchase is still pending.");
  }
  const purchaseToken = tx.purchaseToken?.trim();
  if (!purchaseToken) {
    throw new Error("Google Play did not return a purchase token.");
  }
  return {
    productId: tx.productIdentifier?.trim() || args.productId,
    purchaseToken,
    orderId: tx.orderId || tx.transactionId,
    basePlanId: args.basePlanId,
  };
}

export async function loadPlayPurchasesForRestore(): Promise<PlayVerifyInput[]> {
  assertPlayShell();
  try {
    await playCall("restorePurchases");
  } catch {
    // getPurchases still lists owned items when restore is a no-op.
  }
  const inapp = (await playCall("getPurchases", { productType: "inapp" })) as {
    purchases?: NativeTx[];
  };
  const subs = (await playCall("getPurchases", { productType: "subs" })) as {
    purchases?: NativeTx[];
  };
  const rows = [...(inapp.purchases ?? []), ...(subs.purchases ?? [])];
  const purchases: PlayVerifyInput[] = [];
  for (const row of rows) {
    const productId = row.productIdentifier?.trim();
    const purchaseToken = row.purchaseToken?.trim();
    if (!productId || !purchaseToken) continue;
    if (row.purchaseState && row.purchaseState !== "1") continue;
    purchases.push({
      productId,
      purchaseToken,
      orderId: row.orderId || row.transactionId,
      basePlanId: row.planIdentifier,
    });
  }
  return purchases;
}

export async function openPlaySubscriptionManagement() {
  assertPlayShell();
  await playCall("manageSubscriptions");
}

export function playGrantMessage(result: {
  status: string;
  granted: boolean;
  grantKind: PlayGrant["kind"] | string;
}): string {
  if (result.status === "already_granted" || !result.granted) {
    if (result.grantKind === "academy") return "Course already unlocked.";
    if (result.grantKind === "subscription") return "Plan already active.";
    return "Credits were already added.";
  }
  if (result.grantKind === "credits") {
    return "Google Play payment received. Credits added.";
  }
  if (result.grantKind === "academy") {
    return "Google Play payment received. Course unlocked.";
  }
  return "Google Play payment received. Plan is active.";
}
